<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  platform: {
    type: String,
    required: true,
    validator: v => ['facebook', 'tiktok', 'instagram', 'telegram'].includes(v),
  },
  disabled: { type: Boolean, default: false },
  isLoading: { type: Boolean, default: false },
  type: {
    type: String,
    default: 'button',
    validator: v => ['button', 'submit'].includes(v),
  },
});

const emit = defineEmits(['click']);

const { t } = useI18n();

const PLATFORM = {
  facebook: {
    buttonClass:
      'bg-[#1877F2] hover:enabled:brightness-110 focus-visible:brightness-110',
    icon: 'i-ri-facebook-fill',
    badgeIconClass: 'text-[#1877F2]',
    badgeSpinnerClass: 'text-[#1877F2]',
  },
  instagram: {
    buttonClass:
      'bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#FCAF45] hover:enabled:brightness-110 focus-visible:brightness-110',
    icon: 'i-ri-instagram-fill',
    badgeIconClass: 'text-[#E4405F]',
    badgeSpinnerClass: 'text-[#E4405F]',
  },
  tiktok: {
    buttonClass:
      'bg-[#010101] hover:enabled:brightness-125 focus-visible:brightness-125',
    icon: 'i-ri-tiktok-fill',
    badgeIconClass: 'text-[#010101]',
    badgeSpinnerClass: 'text-[#010101]',
  },
  telegram: {
    buttonClass:
      'bg-[#229ED9] hover:enabled:brightness-110 focus-visible:brightness-110',
    icon: 'i-ri-telegram-fill',
    badgeIconClass: 'text-[#229ED9]',
    badgeSpinnerClass: 'text-[#229ED9]',
  },
};

const label = computed(() => {
  switch (props.platform) {
    case 'facebook':
      return t('INBOX_MGMT.ADD.CHANNEL_CONNECT_ACTION.FACEBOOK');
    case 'instagram':
      return t('INBOX_MGMT.ADD.CHANNEL_CONNECT_ACTION.INSTAGRAM');
    case 'tiktok':
      return t('INBOX_MGMT.ADD.CHANNEL_CONNECT_ACTION.TIKTOK');
    case 'telegram':
      return t('INBOX_MGMT.ADD.CHANNEL_CONNECT_ACTION.TELEGRAM');
    default:
      return '';
  }
});

const palette = computed(() => PLATFORM[props.platform]);

function handleClick(event) {
  if (props.type !== 'submit') {
    emit('click', event);
  }
}
</script>

<template>
  <button
    :type="type"
    :disabled="disabled || isLoading"
    class="inline-flex h-12 min-w-[14rem] shrink-0 items-center justify-center gap-3 rounded-full px-6 text-base font-semibold text-white shadow-sm outline-none transition-all duration-100 ease-out disabled:cursor-not-allowed disabled:opacity-50 active:enabled:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/70"
    :class="palette.buttonClass"
    @click="handleClick"
  >
    <span
      class="flex size-8 shrink-0 items-center justify-center rounded-full bg-white"
    >
      <Spinner
        v-if="isLoading"
        class="!h-5 !w-5 shrink-0"
        :class="palette.badgeSpinnerClass"
      />
      <Icon
        v-else
        :icon="palette.icon"
        class="size-5 shrink-0"
        :class="palette.badgeIconClass"
      />
    </span>
    <span class="min-w-0 truncate">{{ label }}</span>
  </button>
</template>
